import { nanoid } from 'nanoid';
import axios from 'src/utils/axios';
import parseQueryParams from '../../../utils/query';
import { array, number, string } from 'yup';
import { QUESTIONS_TYPES_WITH_CHOICES } from '../../../components/CertificationWorkbench/utils';
import { fDatePost } from 'src/utils/formatTime';
import { datadogRum } from '@datadog/browser-rum';

export const NECESSITIES = {
  REQUIRED: 1,
  DESIRED: 2,
  NOT_REQUIRED: 3,
};

export const SURVEY_PARAMS = parseQueryParams({
  expand: ['household_member'],
  fields: [
    'id',
    'cert_member',
    'household_member',
    'household',
    'household_member.first_name',
    'household_member.last_name',
    'household_member.email',
    'household_member.user',
    'certification',
  ],
});

export const isValidId = (x) => !isNaN(Number(x));

export const getQuestionsParams = (pageId, sectionId) =>
  parseQueryParams({
    page: pageId,
    section: sectionId,
    expand: [
      'answers',
      'answers.answer_group',
      'answers.files.pdf_file_url',
      'answers.files.order',
      'answers.files.status',
      'answers.files.id',
      'answers.files.rejection_reasons',
      'answers.question_choices',
      'qit',
      'qit.question_choices',
      'qit.member_types',
      'qit.parent_qit',
      'qit.parent_choice',
      'qit.question',
      'qit.question.question_choices',
      'qit.parent_qit.question',
      'qit.question_group',
      'qit.question_group.parent_qit',
      'qit.question_group.parent_choice',
    ],
  });

export const getSchemaForRecord = (question_, calculation) => {
  let requiredField = 'This field is required.';
  const question = question_?.qit?.question || calculation?.name;
  const questionType =
    question_?.qit?.question?.question_type || calculation?.format;

  switch (questionType) {
    case 'log_number':
    case 'text':
      if (
        question.name === 'first_name' ||
        question.name === 'last_name' ||
        question.name === 'date_of_birth'
      ) {
        requiredField = `${question.title} is required.`;
      }

      return string().required(
        question.help_text ? question.help_text : requiredField
      );
    case 'date':
      return string().required(
        question.help_text ? question.help_text : 'This field is required.'
      );
    case 'email':
      return string()
        .email('Must be a valid email.')
        .required('Email is required.');
    case 'bool':
      return array()
        .min(1, 'Please select one option.')
        .required('Please select one option.')
        .nullable();
    case 'choices':
      return array()
        .min(1, 'Please select one option.')
        .required('Please select one option.')
        .nullable();
    case 'multiselect':
      return array()
        .min(1, 'Please select at least one option.')
        .required('Please select at least one option.')
        .nullable();
    case 'ssn':
      return string()
        .trim()
        .matches(
          new RegExp('^(\\d{3})-(\\d{2})-(\\d{4})$'),
          'Please enter a valid social security number.'
        )
        .required('Please enter a valid social security number.');
    case 'phone':
      return string()
        .trim()
        .matches(
          new RegExp('^\\(?([0-9]{3})?\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$'),
          'Please enter a valid phone number.'
        )
        .required('Please enter a valid phone number.');
    case 'currency':
    case 'WithoutMoneySignRender':
      return string().test(
        'calc-value-valid',
        'Please enter a valid number',
        (val) => !val || /[\d+]/.test(val)
      );
    case 'file':
      return array().min(1).required(`A files is required.`);
    case 'zip_code':
      return string()
        .trim()
        .matches(
          new RegExp('^(\\d{5})$'),
          'Please enter a valid ZIP Code matches.'
        )
        .required('Please enter a valid ZIP Code.');
    case 'name':
      return string().required('Please enter a valid Name.');
    case 'last_SSN':
      return string().required(requiredField).nullable();
    case 'number':
      return string()
        .trim()
        .matches(
          new RegExp(
            '^(\\$\\s)?-?(([1-9]\\d{0,2}(,\\d{3})*)|0)+(\\.\\d{1,6})?$'
          ),
          'Please enter a valid number.'
        )
        .required('Please enter a valid number.')
        .nullable();
    case 'percentage':
    case 'PercentWithoutSymbol':
      return string()
        .trim()
        .matches(
          new RegExp('^(\\d)+(\\.\\d{1,4})?'),
          'Please enter a valid percentage.'
        )
        .required('Please enter a valid percentage.')
        .nullable();
    case 'frequency':
      // For calculations (no question item), value is a number (choice id);
      // for question items with question_choices, it's an array.
      if (!question_) {
        return number().required('Please select one.').nullable();
      }
      return array()
        .min(1, 'Please select one.')
        .required('Please select one.')
        .nullable();
    default:
      return string();
  }
};

export const getRecordResponseKey = (question) => {
  if (question?.qit) {
    switch (question.qit.question.question_type) {
      case 'log_number':
      case 'date':
      case 'text':
      case 'email':
      case 'ssn':
      case 'last_SSN':
      case 'currency':
      case 'account':
      case 'phone':
      case 'zip_code':
      case 'name':
      case 'number':
      case 'percentage':
      case 'calculation':
        return 'text';
      case 'bool':
      case 'multiselect':
      case 'choices':
      case 'frequency':
        return 'question_choices';
      case 'text_area':
        return 'text_area';
      case 'file':
        return 'file';
      default:
        return null;
    }
  }
  return null;
};

export const buildInitialValues = (records) => {
  const questionGroups = {};
  const orderedQuestions = Object.values(records).sort(
    (a, b) => a.qit.order - b.qit.order
  );
  orderedQuestions.forEach((question) => {
    if (
      question.qit.for_compliance ||
      question.qit.is_hidden ||
      question.qit.question.question_type === 'calculation'
    ) {
      return true;
    }

    // Generate the base form of questions by group
    if (!questionGroups[question.qit.question_group.id]) {
      questionGroups[question.qit.question_group.id] = {
        ...question.qit.question_group,
        formTemplate: {},
        answerGroups: {},
        hasAnswers: false,
        order: question.qit.order,
      };
    }

    if (
      !questionGroups[question.qit.question_group.id].formTemplate[
        'qit_' + question.qit_id
      ]
    ) {
      questionGroups[question.qit.question_group.id].formTemplate[
        'qit_' + question.qit_id
      ] = {
        id: null,
        value: question.qit.question.question_type !== 'file' ? '' : [],
      };
    }
  });

  const valuesByQIT_ = {};
  orderedQuestions.forEach((question) => {
    valuesByQIT_[question.qit?.id] = null;

    if (question.qit.question.question_type === 'file') {
      valuesByQIT_[question.qit?.id] = [];
    }

    question.answers.forEach((answer) => {
      const aG = answer.answer_group;

      if (
        question.qit.for_compliance ||
        question.qit.is_hidden ||
        question.qit.question.question_type === 'calculation'
      ) {
        return true;
      }

      if (!questionGroups[aG.question_group].answerGroups[aG.id]) {
        questionGroups[aG.question_group].answerGroups[aG.id] = {
          id: aG.id,
          records: { ...questionGroups[aG.question_group].formTemplate },
        };

        questionGroups[aG.question_group].hasAnswers = true;
      }

      const answerValue = extractAnswerValue(
        answer,
        question.qit?.question?.question_type || 'text'
      );

      valuesByQIT_[question.qit?.id] = answerValue;
      questionGroups[aG.question_group].answerGroups[aG.id].records[
        'qit_' + question.qit_id
      ] = {
        id: answer.id,
        value: answerValue,
      };
    });
  });

  const initialValues_ = {};
  Object.values(questionGroups).forEach((questionGroup) => {
    initialValues_[questionGroup.id] = questionGroup.hasAnswers
      ? Object.values(questionGroup.answerGroups)
      : // this is adding the extra array to the formTemplate that we see in seasonal employment
        // removing this causes the seasonal employment selector to not even show
        [
          {
            id: nanoid(),
            records: { ...questionGroup.formTemplate },
          },
        ];
  });

  return [questionGroups, initialValues_, valuesByQIT_];
};

const removeListDuplicates = (array) => {
  return [...new Set(array)];
};

export const getExternalParentsValues = async (
  recordsMap,
  sectionId,
  pageId,
  surveyId
) => {
  const questionGroupsWithParents = Object.values(recordsMap)
    .map((record) => record.qit.question_group)
    .filter(
      (qg) =>
        qg?.parent_choice?.id &&
        qg?.parent_qit?.id &&
        qg?.parent_qit?.section !== Number(sectionId) &&
        qg?.parent_qit?.page !== Number(pageId)
    );

  let parentQITsIds = questionGroupsWithParents.map((qg) => qg.parent_qit?.id);
  parentQITsIds = removeListDuplicates(parentQITsIds);
  const params = parseQueryParams({
    expand: ['question_choices'],
    fields: ['id', 'qit', 'question_choices.id', 'question_choices.name'],
    qit__in: parentQITsIds.join(','),
    survey: surveyId,
  });

  const { data } = await axios.get(`questionnaire/survey_answer/?${params}`);

  const answersByQIT = {};
  parentQITsIds.forEach((QITid) => (answersByQIT[QITid] = null));

  data.forEach((a) => {
    if (!answersByQIT[a.qit]) {
      answersByQIT[a.qit] = [];
    }

    if (a.question_choices?.length > 0) {
      const choicesId = answersByQIT[a.qit].map((c) => c.id);
      const notIncludedChoices = a.question_choices.filter(
        (choice) => !choicesId.includes(choice.id)
      );
      answersByQIT[a.qit] = [...answersByQIT[a.qit], ...notIncludedChoices];
    }
  });

  return answersByQIT;
};

export const extractAnswerValue = (answer, questionType) => {
  if (!answer || (answer && answer.is_rejected && questionType !== 'file')) {
    return null;
  }

  switch (questionType) {
    case 'log_number':
    case 'date':
    case 'text':
    case 'email':
    case 'ssn':
    case 'last_SSN':
    case 'currency':
    case 'account':
    case 'phone':
    case 'zip_code':
    case 'name':
    case 'number':
    case 'percentage':
    case 'calculation':
      return answer.text;
    case 'frequency':
    case 'multiselect':
    case 'choices':
    case 'bool':
      return answer.question_choices;
    case 'text_area':
      return answer.text_area;
    case 'file':
      return answer.files ? answer.files : [];
    default:
      return null;
  }
};

export const recordShouldRender = (
  question,
  recordsByQit,
  values,
  questionGroupId,
  answerGroupIndex
) => {
  if (question?.qit?.parent_qit?.question?.question_type === 'file') {
    return false;
  }

  if (question?.qit?.parent_qit?.id && question?.qit?.parent_choice?.id) {
    const parentQitId = recordsByQit[question?.qit?.parent_qit?.id]?.qit_id;
    let parentSelectedChoicesId;

    try {
      const value =
        values[questionGroupId][answerGroupIndex].records['qit_' + parentQitId]
          ?.value;
      const choicesId = value?.length ? value.map((v) => v.id) : null;
      parentSelectedChoicesId =
        choicesId === null || choicesId === undefined ? null : choicesId;
    } catch {
      parentSelectedChoicesId = null;
    }

    const parentChoiceId = question?.qit?.parent_choice?.id;

    return Boolean(
      parentChoiceId &&
        parentSelectedChoicesId &&
        parentSelectedChoicesId?.length > 0 &&
        parentSelectedChoicesId.includes(parentChoiceId)
    );
  }

  return true;
};

export const groupShouldRender = (
  questionGroup,
  flatValuesByQIT_,
  externalParentQITsValues
) => {
  const parentQITid = questionGroup?.parent_qit?.id;
  const parentChoiceId = questionGroup?.parent_choice?.id;

  if (parentQITid && parentChoiceId) {
    const value = (
      flatValuesByQIT_[parentQITid] ||
      externalParentQITsValues[parentQITid] ||
      []
    ).filter(Boolean);
    const parentAnswersId = value?.length > 0 ? value.map((v) => v.id) : null;

    // false parent is not answered
    return Boolean(
      parentChoiceId &&
        parentAnswersId &&
        parentAnswersId?.length > 0 &&
        parentAnswersId.includes(parentChoiceId)
    );
  }

  return true;
};

const getGroupsToDeleteAndFilterVisibleGroups = (
  values,
  pageGroups,
  flatValuesByQIT,
  externalParentQITsValues
) => {
  const groupsToDelete = [];
  const visibleQuestionGroups = Object.entries(values).filter(
    ([questionGroupId, answerGroups]) => {
      const questionGroup = pageGroups[questionGroupId];
      const isVisible = groupShouldRender(
        questionGroup,
        flatValuesByQIT,
        externalParentQITsValues
      );

      if (!isVisible) {
        answerGroups.forEach((answerGroup) => {
          if (isValidId(answerGroup.id)) {
            groupsToDelete.push(answerGroup.id);
          }
        });
      }
      return isVisible;
    }
  );
  return { groupsToDelete, visibleQuestionGroups };
};

const getAnswerGroupsToDeleteAndFilterVisibleRecords = (
  records,
  pageRecordsMap,
  recordsByQit,
  questionGroupId,
  values,
  answerGroupIndex
) => {
  const recordsToDelete = [];

  const visibleRecords = Object.entries(records).filter(
    ([recordKey, answer]) => {
      const question = pageRecordsMap[recordKey.replace('qit_', '')];

      const isVisible = recordShouldRender(
        question,
        recordsByQit,
        values,
        questionGroupId,
        answerGroupIndex
      );

      if (!isVisible) {
        if (isValidId(answer?.id)) {
          recordsToDelete.push(Number(answer.id));
        }
      }

      return isVisible;
    }
  );
  return { recordsToDelete, visibleRecords };
};

const createFilePayloadData = (question, answerGroup, answer) => {
  const answerGroupID = isNaN(answerGroup?.id) ? null : answerGroup?.id;

  const answerID =
    question.answers.find((answer) => answer.answer_group.id === answerGroupID)
      ?.id || null;

  const filePayloadData = {
    survey: question.survey,
    qit_id: question.qit_id,
    answer_group: answerGroupID,
    survey_answer_id: answerID,
    files: [],
  };

  answer.value.forEach((newFile) => {
    if (isValidId(newFile.id) && !newFile.file_blob) {
      return true;
    }
    const payloadData = {
      file_blob: newFile.file_blob,
    };
    if (isValidId(newFile.id)) {
      payloadData.id = newFile.id;
    }

    filePayloadData.files.push(payloadData);
  });
  return filePayloadData.files.length > 0 ? filePayloadData : null;
};

export const buildAnswersPayload = (
  values,
  flatValuesByQIT,
  externalParentQITsValues,
  recordsByQit,
  pageGroups,
  pageRecordsMap
) => {
  const newPayload = [];
  let hasFileAnswers = false;
  const answersToDelete = [];
  const { groupsToDelete, visibleQuestionGroups } =
    getGroupsToDeleteAndFilterVisibleGroups(
      values,
      pageGroups,
      flatValuesByQIT,
      externalParentQITsValues
    );

  (visibleQuestionGroups || []).forEach(([questionGroupId, answerGroups]) => {
    answerGroups.forEach((answerGroup, agIx) => {
      const questionPayload = {
        answer_group: isValidId(answerGroup.id) ? Number(answerGroup.id) : null,
        answers: [],
      };
      newPayload.push(questionPayload);

      const { recordsToDelete, visibleRecords } =
        getAnswerGroupsToDeleteAndFilterVisibleRecords(
          answerGroup.records,
          pageRecordsMap,
          recordsByQit,
          questionGroupId,
          values,
          agIx
        );

      if (answersToDelete.length > 0) {
        answersToDelete.push(...recordsToDelete);
      }

      (visibleRecords || []).forEach(([recordKey, answer]) => {
        const question = pageRecordsMap[recordKey.replace('qit_', '')];

        if (question.qit.question.question_type === 'file') {
          if (!answer.value?.length > 0) {
            return;
          }
          const fileAnswerData = createFilePayloadData(
            question,
            answerGroup,
            answer
          );

          if (!fileAnswerData) {
            return;
          }
          hasFileAnswers = true;
          return questionPayload.answers.push(fileAnswerData);
        }

        const responseKey = getRecordResponseKey(question);
        let answerValue = answer.value;
        if (
          QUESTIONS_TYPES_WITH_CHOICES.includes(
            question.qit.question.question_type
          )
        ) {
          answerValue = answer.value ? answer.value.map((v) => v.id) : [];
        } else if (question.qit.question.question_type === 'currency') {
          answerValue = answerValue.replace(/[^\d.-]/g, '');
        } else if (!answerValue) {
          return true; // For questions that are not choice based, if there is no value, don't save it.
        }

        questionPayload.answers.push({
          survey_answer_id: answer?.id,
          id: answer.id,
          survey: question.survey,
          qit_id: question.qit_id,
          [responseKey]: answerValue,
        });
      });

      // Remove empty objects questions from the payload
      if (questionPayload.answers.length === 0) {
        newPayload.pop();
      }
    });
  });

  return [newPayload, hasFileAnswers, answersToDelete, groupsToDelete];
};

const isInvalidAnswer = (value, questionType) => {
  if (questionType === 'file') {
    return value.length < 1;
  }
  return !value && value !== 0;
};

export const validateFormValues = async (
  values,
  errors,
  pageGroups,
  flatValuesByQIT,
  externalParentQITsValues,
  pageRecordsMap,
  recordsByQit
) => {
  const nagRecords_ = [];
  let newErrors = { ...errors };

  for (const [groupId, answerGroups] of Object.entries(values)) {
    const questionGroup = pageGroups[groupId];
    const groupIsVisible = groupShouldRender(
      questionGroup,
      flatValuesByQIT,
      externalParentQITsValues
    );

    if (!groupIsVisible) {
      continue;
    }

    let agIx = 0;

    for (const answerGroup of answerGroups) {
      for (const [recordKey, answer] of Object.entries(answerGroup.records)) {
        const question = pageRecordsMap[recordKey.replace('qit_', '')];
        const schema = getSchemaForRecord(question);
        const fieldKey = `${groupId}.${agIx}.records.${recordKey}.value`;

        try {
          const isVisible = recordShouldRender(
            question,
            recordsByQit,
            values,
            groupId,
            agIx
          );

          if (
            isVisible &&
            (question.qit?.necessity === NECESSITIES.REQUIRED ||
              (answer?.value &&
                question.qit?.question?.question_type === 'email'))
          ) {
            await schema.validate(answer?.value);
          }

          delete newErrors[fieldKey];

          const isValid = isInvalidAnswer(
            answer.value,
            question.qit.question.question_type
          );

          if (
            isVisible &&
            isValid &&
            question.qit.necessity === NECESSITIES.DESIRED
          ) {
            nagRecords_.push(question);
          }
        } catch (err) {
          newErrors = { ...newErrors, [fieldKey]: err.message };
        }
      }

      agIx += 1;
    }
  }

  return [newErrors, nagRecords_];
};

export const patchCustomProntoFields = (
  flatValuesByQIT,
  recordsByQit,
  certificationId,
  memberId
) => {
  if (!certificationId || !memberId) {
    return;
  }

  Object.values(recordsByQit).forEach((r) => {
    if (
      r?.qit?.question?.question_type === 'date' &&
      r?.qit?.question?.name?.toLowerCase().includes('date of birth') &&
      flatValuesByQIT[r.qit.id]
    ) {
      const dateOfBirth = fDatePost(flatValuesByQIT[r.qit.id]);
      axios.patch(`household/household_member/${memberId}/`, {
        date_of_birth: dateOfBirth,
      });
    } else if (
      r?.qit?.question?.question_type === 'log_number' &&
      flatValuesByQIT[r.qit.id]
    ) {
      axios.patch(`certification/certification/${certificationId}/`, {
        log_number: flatValuesByQIT[r.qit.id],
      });
    }
  });
};

export function datadogAddAction(
  payload = {},
  eventName = 'save_answers_error'
) {
  // send event to data dog to track the errors on the questionnaire
  if (import.meta.env.VITE_DATADOG_ID && import.meta.env.VITE_DATADOG_TOKEN) {
    datadogRum.addAction(eventName, payload);
  }
}

function isValidDate(d) {
  return (
    d instanceof Date &&
    !isNaN(d) &&
    d.getFullYear() >= 1900 &&
    d.getFullYear() < 2100
  );
}

const toUTCDate = (date) => {
  if (!date) {
    return null;
  }

  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
  );
};

export function parseDesktopDatePickerValue(newValue) {
  if (newValue) {
    const { picked, written } = newValue;

    if (picked && !isNaN(picked.getTime()) && isValidDate(picked)) {
      return toUTCDate(picked);
    } else if ((!picked || isNaN(picked.getTime())) && written) {
      // Handle manually written dates
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      if (dateRegex.test(written)) {
        // Create date in UTC
        const [month, day, year] = written.split('/');
        const utcDate = new Date(
          Date.UTC(
            parseInt(year, 10),
            parseInt(month, 10) - 1, // Month is 0-based in Date
            parseInt(day, 10),
            0,
            0,
            0,
            0
          )
        );

        if (isValidDate(utcDate)) {
          return utcDate;
        }
      }
    }
  }

  return null;
}
